type EventDefinition = Record<string, (...args: unknown[]) => unknown>;

export type Query<H extends EventDefinition[string] = EventDefinition[string]> =
  | {
      [Key in keyof Parameters<H>[0]]: Parameters<H>[0][Key] | Parameters<H>[0][Key][];
    }
  | ((...args: Parameters<H>) => boolean);

export type Option = {
  /** 上下文 */
  context?: unknown;
  /** 仅触发一次 */
  once?: boolean;
};

function executeQuery<Q extends Query>(
  query: Q,
  // eslint-disable-next-line @typescript-eslint/ban-types
  ...args: Parameters<Extract<Q, Function>>
): boolean {
  if (args.filter(Boolean).length === 0) {
    return true;
  }

  if (typeof query === 'function') {
    return !!query(...args);
  }
  if (
    typeof query === 'object' &&
    !Array.isArray(query) &&
    query !== null &&
    typeof args[0] === 'object' &&
    !Array.isArray(args[0]) &&
    args[0] === null
  ) {
    return Object.keys(query).every(key => {
      if (Array.isArray(query[key])) {
        return (query[key] as unknown[]).includes(args[0][key]);
      }
      return query[key] === args[0][key];
    });
  }

  return true;
}

/**
 * 事件触发器
 */
export default class EventEmitter<EvtDef extends EventDefinition = EventDefinition> {
  private __handlersMap: {
    [K in keyof EvtDef]: { handler: EvtDef[K]; option?: Option & { query?: Query<EvtDef[K]> } }[];
  };

  constructor() {
    this.__handlersMap = Object.create(null) as this['__handlersMap'];
  }

  /**
   * 判断有没有相关事件类型
   */
  hasType(eventType: string): boolean {
    return typeof this.__handlersMap[eventType] !== 'undefined';
  }

  /**
   * 注册事件
   *
   * @example
   * ```js
   * on(eventType, handler, option?)
   * on(eventType, query, handler, option?)
   * ```
   */
  on<
    K extends keyof EvtDef,
    ARG1 extends EvtDef[K] | Query<EvtDef[K]>,
    ARG2 = ARG1 extends EvtDef[K] ? Option : EvtDef[K],
    ARG3 = ARG2 extends Option ? never : Option
  >(eventType: K, arg1: ARG1, arg2?: ARG2, arg3?: ARG3) {
    let handler: EvtDef[K];
    let option: Option & { query?: Query<EvtDef[K]> };

    if (typeof arg2 === 'function') {
      handler = arg2 as unknown as EvtDef[K];
      option = arg3 || {};
      option.query = arg1;
    } else if (typeof arg1 === 'function') {
      handler = arg1 as EvtDef[K];
      option = arg2 || {};
    } else {
      throw new Error('"handler" must be a function.');
    }

    if (!this.hasType(eventType as string)) {
      this.__handlersMap[eventType] = [];
    }

    const handlers = this.__handlersMap[eventType].slice();
    if (handlers.findIndex(item => item.handler === handler) !== -1) {
      throw new Error('"handler" function is already registered.');
    }

    // ? option need deepCopy
    handlers.push({ handler, option });

    this.__handlersMap[eventType] = handlers;

    return this;
  }

  /**
   * 注册事件（仅触发一次）
   *
   * @example
   * ```js
   * once(eventType, handler, option?)
   * once(eventType, query, handler, option?)
   * ```
   */
  once<
    K extends keyof EvtDef,
    ARG1 extends EvtDef[K] | Query<EvtDef[K]>,
    ARG2 = ARG1 extends EvtDef[K] ? Option : EvtDef[K],
    ARG3 = ARG2 extends Option ? never : Option
  >(eventType: K, arg1: ARG1, arg2?: ARG2, arg3?: ARG3) {
    if (typeof arg2 === 'function') {
      arg3 = arg3 || {};
      arg3.once = true;
    } else if (typeof arg1 === 'function') {
      arg2 = arg2 || {};
      arg2.once = true;
    }

    this.on(eventType, arg1, arg2, arg3);
  }

  /**
   * 取消事件
   */
  off<K extends keyof EvtDef>(eventType: K, handler: EvtDef[K]) {
    if (!this.hasType(eventType as string)) {
      return this;
    }

    const handlers = this.__handlersMap[eventType].slice();
    const index = handlers.findIndex(item => item.handler === handler);

    if (index !== -1) {
      handlers.splice(index, 1);
      this.__handlersMap[eventType] = handlers;
    }

    return this;
  }

  /**
   * 触发事件
   */
  emit<K extends keyof EvtDef>(eventType: K, ...args: Parameters<EvtDef[K]>) {
    if (!this.hasType(eventType as string)) {
      return;
    }

    const handlers = this.__handlersMap[eventType].slice();
    const noOnceHandler: typeof handlers = [];

    handlers.forEach(item => {
      if (item?.option?.query && !executeQuery(item.option.query, ...args)) {
        return;
      }

      item.handler.call(item?.option?.context || this, ...args);

      if (!item?.option?.once) {
        noOnceHandler.push(item);
      }
    });

    this.__handlersMap[eventType] = noOnceHandler;
  }

  //
}
